import { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Image as ImageIcon,
  LoaderCircle,
  Volume2
} from 'lucide-react';
import type { KnowledgeItem } from '../types';
import { lookupWordImages } from '../lib/storage';
import { getWordPhrase } from '../lib/wordPhrases';
import { Modal } from './ui';

type Speak = (text: string, lang: 'en-US' | 'zh-CN', rate?: number) => void;

interface ImageResult {
  images: string[];
  source: string;
  fallbackEmoji: string;
}

const imageCache = new Map<string, ImageResult>();
const imagePreferenceKey = 'memo-growing-word-images';

function readImagePreference() {
  try {
    return window.localStorage.getItem(imagePreferenceKey) !== 'hidden';
  } catch {
    return true;
  }
}

function existingImages(item: KnowledgeItem) {
  return item.imageOptions?.filter(Boolean).length
    ? item.imageOptions.filter(Boolean)
    : item.imageUrl
      ? [item.imageUrl]
      : [];
}

export function WordLearningPanel({
  item,
  onSpeak
}: {
  item: KnowledgeItem;
  onSpeak: Speak;
}) {
  const phrase = useMemo(() => getWordPhrase(item), [item]);
  const initialImages = existingImages(item);
  const [imagesEnabled, setImagesEnabled] = useState(readImagePreference);
  const [imageOptions, setImageOptions] = useState<string[]>(initialImages);
  const [imageIndex, setImageIndex] = useState(0);
  const [fallbackEmoji, setFallbackEmoji] = useState(item.emoji || 'A');
  const [imageSource, setImageSource] = useState(initialImages.length ? '图片知识库' : '');
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const loadImages = async (force = false) => {
    if (!force) {
      const cached = imageCache.get(item.title.toLowerCase());
      if (cached) {
        setImageOptions(cached.images);
        setFallbackEmoji(cached.fallbackEmoji);
        setImageSource(cached.source);
        return;
      }
      const localImages = existingImages(item);
      if (localImages.length) {
        setImageOptions(localImages);
        setImageIndex(0);
        setFallbackEmoji(item.emoji || 'A');
        setImageSource('图片知识库');
        setImageError(false);
        return;
      }
    }

    setImageLoading(true);
    setImageError(false);
    try {
      const result = await lookupWordImages(item.title);
      const next: ImageResult = {
        images: result.images || [],
        source: result.source || '',
        fallbackEmoji: result.fallbackEmoji || item.emoji || 'A'
      };
      imageCache.set(item.title.toLowerCase(), next);
      setImageOptions(next.images);
      setImageIndex(0);
      setFallbackEmoji(next.fallbackEmoji);
      setImageSource(next.source);
    } catch {
      setImageOptions([]);
      setImageIndex(0);
      setFallbackEmoji(item.emoji || 'A');
      setImageSource('');
    } finally {
      setImageLoading(false);
    }
  };

  useEffect(() => {
    setImageOptions(existingImages(item));
    setImageIndex(0);
    setImageError(false);
    setFallbackEmoji(item.emoji || 'A');
    setImageSource(existingImages(item).length ? '图片知识库' : '');
  }, [item]);

  useEffect(() => {
    if (!imagesEnabled) return;
    if (!imageOptions.length) void loadImages();
    // Loading is intentionally tied to the current word and image preference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesEnabled, item.id]);

  const toggleImages = () => {
    const next = !imagesEnabled;
    setImagesEnabled(next);
    try {
      window.localStorage.setItem(imagePreferenceKey, next ? 'visible' : 'hidden');
    } catch {
      // The preference is optional when storage is unavailable.
    }
    if (next && !imageOptions.length) void loadImages();
  };

  const showNextImage = () => {
    if (imageOptions.length > 1) {
      setImageIndex((value) => (value + 1) % imageOptions.length);
      setImageError(false);
      return;
    }
    void loadImages(true);
  };

  const openPicker = () => {
    setPickerOpen(true);
    if (!imageOptions.length) void loadImages();
  };

  const currentImage = imageOptions[imageIndex];
  const imageLabel = imageSource.includes('本地图片缓存') ? '本地图片缓存' : '图片知识库';

  return (
    <div className="word-learning">
      <div className="word-learning__toolbar">
        <button
          className={`image-switch ${imagesEnabled ? 'is-on' : ''}`}
          type="button"
          role="switch"
          aria-checked={imagesEnabled}
          onClick={toggleImages}
        >
          <ImageIcon size={18} />
          <span>图片</span>
          <i aria-hidden="true"><b /></i>
        </button>
        <button
          className="question-button"
          type="button"
          onClick={openPicker}
          disabled={imageLoading}
          aria-label="查看或更换单词图片"
        >
          {imageLoading ? <LoaderCircle className="is-spinning" size={22} /> : <CircleHelp size={23} />}
          <span>换图</span>
        </button>
      </div>

      <div className={`word-learning__hero ${imagesEnabled ? 'has-image' : ''}`}>
        <div className="word-learning__text">
          <span className="knowledge-label">认一认这个单词</span>
          <div className="word-title-row">
            <strong className="word-title">{item.title}</strong>
            <button
              className="sound-button"
              type="button"
              onClick={() => onSpeak(item.title, 'en-US')}
              aria-label={`播放单词 ${item.title}`}
            >
              <Volume2 size={25} />
            </button>
          </div>
          {item.meaning ? <h2>{item.meaning}</h2> : null}
        </div>

        {imagesEnabled ? (
          <button
            className="word-preview"
            type="button"
            onClick={openPicker}
            aria-label={`查看 ${item.title} 的图片`}
          >
            {imageLoading ? (
              <span className="word-preview__loading"><LoaderCircle className="is-spinning" size={26} />找图片</span>
            ) : currentImage && !imageError ? (
              <img
                src={currentImage}
                alt={item.meaning || item.title}
                onError={() => setImageError(true)}
              />
            ) : (
              <span className="word-preview__fallback">{fallbackEmoji}</span>
            )}
          </button>
        ) : null}
      </div>

      <section className="word-phrase-card">
        <div className="word-phrase-card__copy">
          <span>简单短句</span>
          <strong>{phrase.en}</strong>
          <p>{phrase.zh}</p>
        </div>
        <button
          className="sound-button sound-button--phrase"
          type="button"
          onClick={() => onSpeak(phrase.en, 'en-US')}
          aria-label={`播放短句 ${phrase.en}`}
        >
          <Volume2 size={23} />
        </button>
      </section>

      <Modal
        open={pickerOpen}
        title={`${item.title} 的图片`}
        subtitle={imageSource.includes('本地图片缓存')
          ? '已缓存到本地，可更换一张'
          : '来自图片知识库，可更换一张'}
        onClose={() => setPickerOpen(false)}
        size="large"
      >
        <div className="word-image-viewer">
          {imageLoading ? (
            <div className="image-loading">
              <LoaderCircle className="is-spinning" size={30} />
              <strong>正在寻找匹配图片...</strong>
            </div>
          ) : currentImage && !imageError ? (
            <img src={currentImage} alt={`${item.title} 的联想图片`} onError={() => setImageError(true)} />
          ) : (
            <div className="image-fallback">
              <span>{fallbackEmoji}</span>
              <strong>{item.meaning || item.title}</strong>
              <small>暂未找到合适图片</small>
            </div>
          )}
          <div className="word-image-viewer__footer">
            <span>
              <ImageIcon size={17} />
              {imageOptions.length ? ` ${imageIndex + 1} / ${imageOptions.length} · ${imageLabel}` : ' 图片知识库'}
            </span>
            <div>
              <button
                className="secondary-button"
                type="button"
                disabled={!imageOptions.length}
                onClick={() => {
                  setImageIndex((value) => (value - 1 + imageOptions.length) % imageOptions.length);
                  setImageError(false);
                }}
              >
                <ChevronLeft size={18} />
                上一张
              </button>
              <button className="secondary-button" type="button" onClick={showNextImage}>
                换一张
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
